/**
 * @fileoverview Flashcard Playback Mode Redux Slice
 *
 * Manages playback mode state for flashcard audio player including:
 * - Mode switching (vocab/wholeSong)
 * - Flashcard timing boundaries
 *
 * **Playback Modes:**
 * - vocab: Single region with dynamic end-time
 * - wholeSong: All flashcard regions visible, unrestricted playback
 *
 * **State Structure:**
 * All state is serializable (primitives only) for Redux DevTools and testing.
 *
 * @module store/slices/flashcardPlaybackSlice
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { RootState } from '../store'

export type PlaybackMode = 'vocab' | 'wholeSong'

export interface FlashcardPlaybackState {
  /** Current playback mode */
  playbackMode: PlaybackMode
  /** Current flashcard start time in milliseconds */
  currentFlashcardStartMs: number | null
  /** Current flashcard end time in milliseconds */
  currentFlashcardEndMs: number | null
}

const initialState: FlashcardPlaybackState = {
  playbackMode: 'vocab',
  currentFlashcardStartMs: null,
  currentFlashcardEndMs: null,
}

const flashcardPlaybackSlice = createSlice({
  name: 'flashcardPlayback',
  initialState,
  reducers: {
    /**
     * Set playback mode (vocab/wholeSong)
     */
    setPlaybackMode: (state, action: PayloadAction<PlaybackMode>) => {
      state.playbackMode = action.payload
    },

    /**
     * Update flashcard timing boundaries
     */
    setFlashcardBoundaries: (
      state,
      action: PayloadAction<{
        startMs: number
        endMs: number
      }>
    ) => {
      const { startMs, endMs } = action.payload

      state.currentFlashcardStartMs = startMs
      state.currentFlashcardEndMs = endMs
    },

    /**
     * Clear all playback state
     */
    clearPlaybackState: (state) => {
      state.playbackMode = 'vocab'
      state.currentFlashcardStartMs = null
      state.currentFlashcardEndMs = null
    },
  },
})

export const {
  setPlaybackMode,
  setFlashcardBoundaries,
  clearPlaybackState,
} = flashcardPlaybackSlice.actions

export default flashcardPlaybackSlice.reducer

/** Selectors */
export const selectPlaybackMode = (state: RootState) =>
  state.flashcardPlayback.playbackMode

export const selectFlashcardBoundaries = (state: RootState) => ({
  startMs: state.flashcardPlayback.currentFlashcardStartMs,
  endMs: state.flashcardPlayback.currentFlashcardEndMs,
})
